/**
 * The card back, painted once and used by both things that draw one.
 *
 * There are two: the card slab inside the pack model, which slides out of the
 * mouth in 3D, and the DOM card that takes over from it the instant it clears.
 * The handoff only works if they are the same picture — the whole trick is that
 * both objects are in the same place at the same moment, and a viewer who spots
 * the artwork change has seen the seam the handoff exists to hide.
 *
 * They were not the same picture. The slab painted a glowing diamond on
 * `#0c0c0e` with single-direction stripes; the DOM back was `#141418` with a
 * fine crosshatch and the flask mark. This module is the fix: one painter, one
 * canvas, consumed as a WebGL texture by one and as a background image by the
 * other, so they cannot drift again.
 *
 * Deliberately free of any three.js import. `CardFace` renders on the
 * dashboard, on profiles and in the vault, and none of those pages should pull
 * a 3D engine into their bundle to draw a rectangle.
 */

/** Matches the card mesh's UV layout. Do not change without re-checking the 3D. */
export const CARD_BACK_WIDTH = 512
export const CARD_BACK_HEIGHT = 716

const MARK_SRC = '/brand/exlyr-mark-256.png'

/**
 * Paints the back at whatever size the canvas is.
 *
 * `mark` is optional so the first paint can happen synchronously — the texture
 * goes up immediately with everything except the logo, and is repainted once
 * the PNG decodes. A card that appears a frame early without its mark beats one
 * that appears late with it.
 */
export function paintCardBack(
  x: CanvasRenderingContext2D,
  w: number,
  h: number,
  mark: HTMLImageElement | null,
): void {
  x.clearRect(0, 0, w, h)

  // `--color-surface-2`, the same ground the DOM back has always used.
  x.fillStyle = '#141418'
  x.fillRect(0, 0, w, h)

  /* Crosshatch, both diagonals. The DOM version is a pair of repeating
     gradients at 1px every 8px; at this resolution that is a 2px line every
     18px, which lands on the same visual density once the card is drawn at the
     size it is actually seen. */
  x.strokeStyle = 'rgba(48,248,16,0.055)'
  x.lineWidth = 2
  const step = 18
  for (let i = -h; i < w + h; i += step) {
    x.beginPath()
    x.moveTo(i, 0)
    x.lineTo(i + h, h)
    x.stroke()

    x.beginPath()
    x.moveTo(i, h)
    x.lineTo(i + h, 0)
    x.stroke()
  }

  // Hairline frame, echoing the 1px border the DOM card carries.
  const inset = w * 0.055
  x.strokeStyle = 'rgba(255,255,255,0.10)'
  x.lineWidth = 2
  x.strokeRect(inset, inset, w - inset * 2, h - inset * 2)

  const cx = w / 2
  const cy = h / 2

  // A soft green bloom behind the mark, so the centre has some depth under the
  // 3D lighting rather than reading as a flat sticker.
  const glow = x.createRadialGradient(cx, cy, 0, cx, cy, w * 0.42)
  glow.addColorStop(0, 'rgba(48,248,16,0.20)')
  glow.addColorStop(1, 'rgba(48,248,16,0)')
  x.fillStyle = glow
  x.beginPath()
  x.arc(cx, cy, w * 0.42, 0, Math.PI * 2)
  x.fill()

  if (mark) {
    const size = w * 0.34
    x.drawImage(mark, cx - size / 2, cy - size / 2 - h * 0.03, size, size)
  }

  x.fillStyle = 'rgba(250,250,250,0.42)'
  x.font = `600 ${Math.round(w * 0.052)}px Inter, system-ui, sans-serif`
  x.textAlign = 'center'
  x.letterSpacing = `${Math.round(w * 0.018)}px`
  x.fillText('ELYXR', cx, cy + w * 0.28)
}

/** Resolves once the mark has decoded, or immediately to null if it cannot. */
let markPromise: Promise<HTMLImageElement | null> | null = null

export function loadMark(): Promise<HTMLImageElement | null> {
  if (markPromise) return markPromise
  markPromise = new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    // No mark is a plainer card, not a broken one.
    img.onerror = () => resolve(null)
    img.src = MARK_SRC
  })
  return markPromise
}

/**
 * The finished back as a canvas: painted immediately, repainted with the mark.
 *
 * Callers get the canvas synchronously and a promise that fires once it is
 * complete, because the 3D texture needs to know when to flag `needsUpdate`
 * and the DOM needs to know when the data URL is worth reading.
 */
export function createCardBackCanvas(): {
  canvas: HTMLCanvasElement
  complete: Promise<HTMLCanvasElement>
} {
  const canvas = document.createElement('canvas')
  canvas.width = CARD_BACK_WIDTH
  canvas.height = CARD_BACK_HEIGHT
  const ctx = canvas.getContext('2d')!

  paintCardBack(ctx, canvas.width, canvas.height, null)

  const complete = loadMark().then((mark) => {
    if (mark) paintCardBack(ctx, canvas.width, canvas.height, mark)
    return canvas
  })

  return { canvas, complete }
}

/**
 * The same artwork as a data URL, for the DOM card.
 *
 * Memoised: every revealed card in a ten-card rip asks for this, and painting
 * and encoding a 512×716 PNG per card would be paid ten times for one image.
 */
let urlPromise: Promise<string> | null = null

export function cardBackUrl(): Promise<string> {
  if (urlPromise) return urlPromise
  urlPromise = createCardBackCanvas().complete.then((canvas) => canvas.toDataURL('image/png'))
  return urlPromise
}
